import React, { useState, useEffect } from "react";
import { UserService } from "../../services/users/userService";
import { Plus, Search, User, Trash2, Edit2, Mail, Phone, CheckCircle, XCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ConfirmModal } from "../../components/common/Modals/ConfirmModal";

interface UserType {
  id: string;
  email: string;
  role: string;
  telephone: string;
  is_verified: boolean;
  created_at: string;
  profile?: {
    entreprise_nom?: string;
  };
}

export const AdminUtilisateurs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id);
  
  // Form State
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    password_confirm: "",
    role: "finance",
    telephone: ""
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await UserService.getUsers();
      setUsers(response.data.results || response.data || []);
    } catch (error) {
      console.error("Erreur chargement utilisateurs", error);
      toast.error(t('paiements.messages.errorLoad')); // Use common or specific
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const emptyForm = { email: "", password: "", password_confirm: "", role: "finance", telephone: "" };

  const openCreate = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (u: UserType) => {
    setEditingUser(u);
    setFormData({ ...emptyForm, email: u.email, role: u.role, telephone: u.telephone || "" });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      setDeleting(true);
      await UserService.deleteUser(userToDelete.id);
      toast.success(t('users.delete.success'));
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error("Erreur suppression utilisateur", error);
      toast.error(t('users.delete.error'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      try {
        await UserService.updateUser(editingUser.id, { role: formData.role, telephone: formData.telephone });
        toast.success(t('users.edit.success'));
        closeModal();
        fetchUsers();
      } catch (error) {
        console.error("Erreur modification utilisateur", error);
        toast.error(t('users.edit.error'));
      }
      return;
    }
    try {
      await UserService.createUser(formData);
      toast.success(t('users.create.success'));
      closeModal();
      setFormData(emptyForm);
      fetchUsers();
    } catch (error: any) {
      console.error("Erreur création utilisateur", error);
      let errorMessage = t('users.create.error');
      if (error.response && error.response.data) {
        // Format validation errors
        if (typeof error.response.data === 'object') {
           errorMessage = Object.entries(error.response.data)
             .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
             .join('\n');
        } else {
           errorMessage = String(error.response.data);
        }
      }
      toast.error(errorMessage);
    }
  };

  const filteredUsers = users.filter((u) => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.profile?.entreprise_nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role: string) => {
    switch(role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'super_admin': return 'bg-purple-100 text-purple-800';
      case 'finance': return 'bg-blue-100 text-blue-800';
      case 'client': return 'bg-green-100 text-green-800';
      case 'fournisseur': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('users.overview')}</p>
        </div>
        <button 
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
             <Plus className="h-4 w-4 mr-2" /> {t('users.newUser')}
        </button>
      </div>

      {/* Stats Cards could go here */}

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
           <div className="relative max-w-xs">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder={t('header.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
           </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.table.user')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.table.role')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.table.status')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.table.createdAt')}
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                 <tr>
                   <td colSpan={5} className="px-6 py-4 text-center">
                     <div className="flex justify-center">
                       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                     </div>
                   </td>
                 </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                   <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                     {t('common.noResult')}
                   </td>
                 </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                            {user.email.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.profile?.entreprise_nom || user.email.split('@')[0]}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center">
                            <Mail className="h-3 w-3 mr-1" /> {user.email}
                          </div>
                          {user.telephone && (
                            <div className="text-xs text-gray-400 flex items-center mt-0.5">
                                <Phone className="h-3 w-3 mr-1" /> {user.telephone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       {user.is_verified ? (
                           <span className="inline-flex items-center text-green-800 text-xs font-medium">
                               <CheckCircle className="h-4 w-4 mr-1 text-green-500" /> {t('users.table.verified')}
                           </span>
                       ) : (
                           <span className="inline-flex items-center text-gray-500 text-xs font-medium">
                               <XCircle className="h-4 w-4 mr-1 text-gray-400" /> {t('users.table.notVerified')}
                           </span>
                       )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(user.created_at), "dd MMM yyyy", { locale: i18n.language.startsWith('fr') ? fr : enUS })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEdit(user)}
                        title={t('users.edit.title')}
                        className="text-primary-600 hover:text-primary-900 mx-2"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {/* Impossible de supprimer son propre compte */}
                      {String(user.id) !== String(currentUserId) && (
                        <button
                          onClick={() => setUserToDelete(user)}
                          title={t('users.delete.title')}
                          className="text-red-600 hover:text-red-900 mx-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Création */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={closeModal}
                >
                  <span className="sr-only">Fermer</span>
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <User className="h-6 w-6 text-primary-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    {editingUser ? t('users.edit.title') : t('users.create.title')}
                  </h3>
                  <div className="mt-2">
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('users.create.email')}</label>
                        <input
                          type="email"
                          required
                          disabled={!!editingUser}
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('users.create.role')}</label>
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({...formData, role: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        >
                          <option value="finance">Agent Finance</option>
                          <option value="admin">Administrateur</option>
                          <option value="client">Client</option>
                          <option value="fournisseur">Fournisseur</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('users.create.phone')}</label>
                        <input
                          type="tel"
                          value={formData.telephone}
                          onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      {!editingUser && (
                      <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('users.create.password')}</label>
                        <input
                          type="password"
                          required
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('users.create.confirmPassword')}</label>
                        <input
                          type="password"
                          required
                          value={formData.password_confirm}
                          onChange={(e) => setFormData({...formData, password_confirm: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      </>
                      )}
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                          {editingUser ? t('common.save') : t('common.create')}
                        </button>
                        <button
                          type="button"
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                          onClick={closeModal}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteUser}
        title={t('users.delete.title')}
        message={t('users.delete.message', { email: userToDelete?.email || "" })}
        confirmText={t('users.delete.confirm')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};
